import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtDate, mediaUrl } from "../../lib/api";
import { PublicNav, PublicFooter } from "../../components/public/PublicNav";
import { AdSlot } from "../../components/public/AdSlot";
import { ShareButtons } from "../../components/public/ShareButtons";
import { useSeo } from "../../hooks/useSeo";
import { ChevronLeft } from "lucide-react";

export const PublicArticle = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useSeo({
    title: article ? `${article.title} | Rouge Première` : "Article | Rouge Première",
    description: article?.subtitle || article?.excerpt || "Article éditorial Rouge Première — cinéma & séries.",
    ogImage: article?.cover_image_url ? mediaUrl(article.cover_image_url) : undefined,
  });

  useEffect(() => {
    api.get(`/articles/${slug}`)
      .then((r) => {
        setArticle(r.data);
        if (!localStorage.getItem("rp_token")) {
          api.post("/track/article-view", { slug }).catch(() => {});
        }
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div>
        <PublicNav />
        <div className="max-w-2xl mx-auto text-center py-32 px-5">
          <div className="font-display text-5xl">404</div>
          <p className="text-neutral-500 mt-3">Cet article est introuvable.</p>
          <Link to="/articles" className="btn-red mt-6">Voir tous les articles</Link>
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (!article) {
    return (
      <div>
        <PublicNav />
        <div className="max-w-3xl mx-auto px-5 py-16 space-y-6">
          <div className="h-8 w-2/3 bg-neutral-100 rounded animate-pulse" />
          <div className="aspect-[16/9] bg-neutral-100 rounded animate-pulse" />
          <div className="h-4 w-full bg-neutral-100 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <PublicNav />

      <article className="max-w-[860px] mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-20">
        <Link
          to="/articles"
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.25em] text-neutral-500 hover:text-[var(--red)]"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Retour aux articles
        </Link>

        {article.tags?.length > 0 && (
          <div className="mt-6 text-[11px] uppercase tracking-[0.3em] text-[var(--red)]">
            {article.tags.join(" · ")}
          </div>
        )}

        <h1 className="font-display text-4xl md:text-6xl leading-[1.05] mt-3">
          {article.title}
        </h1>
        {article.subtitle && (
          <p className="text-neutral-600 mt-5 text-lg leading-relaxed max-w-2xl">
            {article.subtitle}
          </p>
        )}
        <div className="mt-6 text-[11px] uppercase tracking-widest text-neutral-400">
          par {article.author_display || article.author_name || article.author_email} · {fmtDate(article.published_at || article.created_at)}
        </div>
        {(article.author_display || article.author_name || "").trim().toLowerCase() === "emmanuel" && (
          <div className="mt-1 text-[10px] uppercase tracking-[0.3em] text-neutral-400 italic">
            Directeur de la publication
          </div>
        )}

        {article.cover_image_url && (
          <figure className="mt-10 flex items-center justify-center rounded-md overflow-hidden border border-[var(--line)] bg-neutral-50 py-4">
            <img
              src={article.cover_image_url}
              alt={article.title}
              className="w-auto max-w-full max-h-[400px] object-contain"
            />
          </figure>
        )}

        <div className="prose-editorial mt-10">
          {article.blocks.map((b, i) => (
            <BlockRenderer key={i} block={b} />
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-[var(--line)]">
          <ShareButtons
            url={`${process.env.REACT_APP_BACKEND_URL}/api/og-v2/article/${article.slug}`}
            title={article.title}
            description={article.subtitle || article.excerpt || ""}
          />
        </div>
      </article>

      <AdSlot slot="article-bottom" className="max-w-[860px] mx-auto px-5 md:px-8 mb-16" />

      <PublicFooter />
    </div>
  );
};

const BlockRenderer = ({ block }) => {
  switch (block.type) {
    case "heading":
      return <h2>{block.text}</h2>;
    case "quote":
      return <blockquote>{block.text}</blockquote>;
    case "image":
      return (
        <figure className="my-8 flex flex-col items-center">
          <img
            src={mediaUrl(block.url)}
            alt={block.caption || ""}
            className="w-auto max-w-full md:max-w-[560px] max-h-[500px] object-contain rounded-md border border-[var(--line)] bg-neutral-50"
          />
          {block.caption && (
            <figcaption className="mt-3 text-xs text-neutral-500 italic text-center max-w-[560px]">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );
    case "video":
      return <VideoBlock block={block} />;
    default:
      return <p>{block.text}</p>;
  }
};

const VideoBlock = ({ block }) => {
  const src = block.url || "";
  const isYT = /youtube\.com|youtu\.be/.test(src);
  const isVimeo = /vimeo\.com/.test(src);
  if (block.embed || isYT || isVimeo) {
    let embedUrl = src;
    if (isYT) {
      const idMatch = src.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{6,})/);
      if (idMatch) embedUrl = `https://www.youtube.com/embed/${idMatch[1]}`;
    } else if (isVimeo) {
      const idMatch = src.match(/vimeo\.com\/(\d+)/);
      if (idMatch) embedUrl = `https://player.vimeo.com/video/${idMatch[1]}`;
    }
    return (
      <figure>
        <div className="aspect-video rounded-md overflow-hidden border border-[var(--line)] bg-black">
          <iframe
            src={embedUrl}
            title={block.caption || "vidéo"}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        {block.caption && <figcaption>{block.caption}</figcaption>}
      </figure>
    );
  }
  return (
    <figure className="my-8">
      <video
        data-testid="article-video"
        src={mediaUrl(src)}
        controls
        playsInline
        preload="metadata"
        className="w-full max-w-[720px] mx-auto rounded-md border border-[var(--line)] bg-black"
      />
      {block.caption && (
        <figcaption className="mt-2 text-xs text-neutral-500 italic text-center">
          {block.caption}
        </figcaption>
      )}
    </figure>
  );
};
